import React, { ReactElement, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Keyboard, ScrollView, StyleSheet } from 'react-native';
import * as z from 'zod';
import {
    Theme,
    useConfirmDiscard,
    useThemeStyle,
    WW,
    isAppError,
    showToast,
    isNetworkError,
    useApartmentColumnOrder,
} from '_utils';
import handleInvalidSubmit from '_utils/handleInvalidSubmit';
import { useSelectedCoopItem } from '_utils/hooks';
import { optionalNumberInput } from '_utils/zod';
import { ControlledInput, FormInput, HeaderWithNav, PrimaryButton } from 'Components';
import { Apartment, buildAddress, buildLegalName } from 'types/Apartment';
import DropdownSelector from './components/DropdownSelector';
import { useEditApartment } from '../../_api/useApartments';
import { SafeAreaView } from '../../_dependencies/safeArea';
import { useAppNavigation } from '../../_navigator';

interface HomeEditProps {
    route: { params: { apartment: Apartment } };
}

const staticFormSchema = z.object({
    legal_name: z.string(),
    project_name: z.string().max(255).min(1),
    address: z.string(),
    type: z.enum(['apartment', 'detached_house', 'semi_detached_house', 'room']),
    floor: optionalNumberInput,
    area: optionalNumberInput,
    living_area: optionalNumberInput,
    rooms: optionalNumberInput,
    parking_spot: z.string().max(255),
});

const generateFormSchema = (order: ReturnType<typeof useApartmentColumnOrder>) =>
    staticFormSchema.merge(
        z.object({
            [order[0]]: z.string().max(255).min(1),
        }),
    );

type FormValues = Required<z.infer<typeof staticFormSchema>>;

const mapToDefault = (initial: Apartment): FormValues => {
    return {
        project_name: initial.project_name ?? '',
        legal_name: initial.legal_name ?? '',
        address: initial.address ?? '',
        type: initial.type ?? 'apartment',
        floor: initial.floor ?? 0,
        area: initial.area ?? 0,
        living_area: initial.living_area ?? 0,
        rooms: initial.rooms ?? 0,
        parking_spot: initial.parking_spot ?? '',
    };
};

function defaultIfFalsy<T>(value: '' | number | undefined, defaultVal: T) {
    if (value) {
        return value;
    }
    return defaultVal;
}

const HomeEdit = ({ route }: HomeEditProps): ReactElement => {
    const selectedCoop = useSelectedCoopItem();
    const themedStyle = useThemeStyle(styles);
    const { t } = useTranslation();
    const { goBack: internalGoBack } = useAppNavigation();
    const { isPending: isLoading, mutate: editApartment } = useEditApartment();

    const order = useApartmentColumnOrder(selectedCoop?.apartment_display ?? 'project_name');
    const { formState, watch, setValue, setError, ...methods } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: mapToDefault(route.params.apartment),
        resolver: zodResolver(useMemo(() => generateFormSchema(order), [order])),
    });

    const { isValid } = formState;

    const { content } = useConfirmDiscard(formState.isDirty && !isLoading, true, internalGoBack);

    const buttonStatus = useMemo(() => {
        return isLoading ? 'loading' : isValid ? null : 'disabled';
    }, [isLoading, isValid]);

    const onSubmit: SubmitHandler<FormValues> = (formValues) => {
        const apartmentId = route.params.apartment.id;
        editApartment(
            [
                apartmentId,
                {
                    ...formValues,
                    area: defaultIfFalsy(formValues.area, 0),
                    living_area: defaultIfFalsy(formValues.living_area, 0),
                    floor: defaultIfFalsy(formValues.floor, 0),
                    rooms: defaultIfFalsy(formValues.rooms, 0),
                },
            ],
            {
                onSuccess: () => {
                    internalGoBack();
                },
                onError: (error) => {
                    Keyboard.dismiss();
                    if (isAppError(error)) {
                        if (isNetworkError(error)) {
                            return;
                        }

                        const duplicateNames = order.filter(
                            (key) => error.response?.data.errors && key in error.response?.data.errors,
                        );
                        if (duplicateNames.length > 0) {
                            duplicateNames.forEach((key) => {
                                const message = t('editHome:codeExists', {
                                    param: t(`myHome:${key}`),
                                    coopType: t(
                                        `typeSpecific:${selectedCoop?.type ?? 'joint_ownership'}:coopTypeDefiniteArticle`,
                                    ),
                                });
                                showToast({ header: message, text: '', type: 'error' });
                                setError(key, { message, type: 'manual' });
                            });
                            return;
                        }
                    }
                    showToast({
                        header: t('global:error_saving_header'),
                        text: t('global:error_saving_description'),
                        type: 'error',
                    });
                },
            },
        );
    };

    return (
        <SafeAreaView style={themedStyle.full} edges={['bottom', 'left', 'right']}>
            <HeaderWithNav style={themedStyle.navHeader} title={t('editHome:title')} />
            <ScrollView style={themedStyle.content} keyboardShouldPersistTaps="always">
                <FormProvider setValue={setValue} watch={watch} formState={formState} setError={setError} {...methods}>
                    {order
                        .map((key) => {
                            const renderInput = (
                                renderKey: (typeof order)[number] | 'section_number' | 'share_number',
                                staticValue: string,
                            ) => {
                                const Component = staticValue ? ControlledInput : FormInput;

                                return (
                                    <Component
                                        readOnly={Boolean(staticValue)}
                                        explainer={
                                            staticValue
                                                ? t('editHome:read_only', { label: t(`myHome:${renderKey}`) })
                                                : undefined
                                        }
                                        key={renderKey}
                                        name={renderKey}
                                        label={t(`myHome:${renderKey}`) + ':'}
                                        value={staticValue}
                                        onChange={() => {}}
                                    />
                                );
                            };
                            const postalAddress = route.params.apartment.norwegian_postal_address;
                            if (postalAddress && key !== 'project_name') {
                                if (key === 'address') {
                                    if (
                                        postalAddress.street_name ||
                                        postalAddress.house_number ||
                                        postalAddress.house_letter ||
                                        postalAddress.unit_number ||
                                        postalAddress.postal_area ||
                                        postalAddress.postal_code
                                    ) {
                                        return renderInput('address', buildAddress(postalAddress));
                                    }
                                }
                                // Is legal_name
                                const arr = [];
                                if (postalAddress.section_number) {
                                    arr.push(
                                        renderInput('section_number', buildLegalName(postalAddress, 'section_number')),
                                    );
                                }
                                if (postalAddress.share_number) {
                                    arr.push(
                                        renderInput('share_number', buildLegalName(postalAddress, 'share_number')),
                                    );
                                }
                                return arr;
                            }
                            return renderInput(key, '');
                        })
                        .flat()}
                    <DropdownSelector
                        items={[
                            { name: t('myHome:detached_house'), id: 'detached_house' },
                            { name: t('myHome:semi_detached_house'), id: 'semi_detached_house' },
                            { name: t('myHome:apartment'), id: 'apartment' },
                            { name: t('myHome:room'), id: 'room' },
                        ]}
                    />
                    <FormInput type="number" name="floor" label={t('myHome:floor')} />
                    <FormInput type="number" name="area" label={t('myHome:area')} suffix="m²" />
                    <FormInput type="number" name="living_area" label={t('myHome:living_area')} suffix="m²" />
                    <FormInput type="number" name="rooms" label={t('myHome:bedrooms')} />
                    <FormInput name="parking_spot" label={t('myHome:parking_spot')} />
                </FormProvider>
            </ScrollView>
            <PrimaryButton
                status={buttonStatus}
                text={t('editHome:save')}
                onPress={methods.handleSubmit(onSubmit, handleInvalidSubmit(t))}
                allowDisabledPress={buttonStatus !== 'loading'}
                bottomAction
            />
            {content}
        </SafeAreaView>
    );
};

const styles = (theme: Theme) =>
    StyleSheet.create({
        full: {
            height: '100%',
            backgroundColor: theme.mainBackground,
        },
        navHeader: {
            backgroundColor: theme.mainBackground,
        },
        content: {
            flex: 1,
            marginLeft: WW * 0.04,
            marginRight: WW * 0.04,
        },
    });

export default HomeEdit;
